import {
  Alert,
  AlertIcon,
  AlertTitle,
  Button as ChakraButton,
  CloseButton,
  Flex,
} from '@chakra-ui/react';
import { IconArrowUndoUp } from 'central-icons/IconArrowUndoUp';
import { IconCircleInfo } from 'central-icons/IconCircleInfo';
import { IconCrossSmall } from 'central-icons/IconCrossSmall';
import React from 'react';

import { useChartTheme } from '@revelio/replots';
import { Button, type ToastId } from '@revelio/ui';

import { useToast } from '../hooks/useToast';
import styles from './UndoToast.module.css';

interface ShowUndoToastOptions {
  message: string;
  onUndo: () => void;
  duration?: number;
}

export const useUndoToast = () => {
  const toast = useToast();
  const toastIdRef = React.useRef<ToastId | undefined>();

  const showUndoToast = ({
    message,
    onUndo,
    duration = 5000,
  }: ShowUndoToastOptions) => {
    const handleUndoClick = () => {
      onUndo();

      if (toastIdRef.current) {
        toast.update(toastIdRef.current, {
          status: 'info',
          duration: 2000,
          title: 'Page restored.',
          isClosable: true,
        });
      }
    };

    if (toastIdRef.current && toast.isActive(toastIdRef.current)) {
      toast.close(toastIdRef.current);
    }

    toastIdRef.current = toast({
      position: 'bottom',
      duration,
      render: ({ onClose }) => (
        <UndoToast
          message={message}
          onUndoClick={handleUndoClick}
          onClose={onClose}
        />
      ),
    });
  };

  return showUndoToast;
};

interface UndoToastProps {
  message: string;
  onUndoClick: () => void;
  onClose: () => void;
}

export const UndoToast = ({
  message,
  onUndoClick,
  onClose,
}: UndoToastProps) => {
  const { isNewTheme } = useChartTheme();

  if (!isNewTheme) {
    return (
      <Alert
        status="info"
        variant="solid"
        alignItems="center"
        borderRadius="md"
        boxShadow="md"
      >
        <AlertIcon />
        <Flex flex="1" align="center" justify="space-between">
          <AlertTitle mr={4}>{message}</AlertTitle>
          <ChakraButton
            leftIcon={<IconArrowUndoUp />}
            colorScheme="white"
            onClick={onUndoClick}
            size="sm"
            height="22px"
            mr={6}
          >
            Undo
          </ChakraButton>
          <CloseButton
            size="sm"
            onClick={onClose}
            position="absolute"
            insetEnd={1}
            top={1}
          />
        </Flex>
      </Alert>
    );
  }

  return (
    <div className={styles.toast} role="status">
      <span className={styles.icon}>
        <IconCircleInfo size="20px" />
      </span>
      <div className={styles.message}>{message}</div>
      <div className={styles.actions}>
        <Button
          size="xs"
          variant="ghost"
          color="lightBlue"
          leftIcon={<IconArrowUndoUp size="14px" />}
          onClick={onUndoClick}
        >
          Undo
        </Button>
        <button
          type="button"
          className={styles.close}
          aria-label="Close"
          onClick={onClose}
        >
          <IconCrossSmall size="14px" />
        </button>
      </div>
    </div>
  );
};
